import React, { useState } from 'react';
import { X, User, Building2, MapPin, Phone } from 'lucide-react';

const PlanFormModal = ({ isOpen, onClose, selectedPlan }) => {
  const [formData, setFormData] = useState({
    // Dados da empresa
    nome: '',
    cnpj: '',
    cei: '',
    inscricaoEstadual: '',
    cnae: '',
    grauRisco: '',
    
    // Endereço
    cep: '',
    endereco: '',
    numero: '',
    complemento: '',
    estado: '',
    cidade: '',
    bairro: '',
    
    // Contato
    telefone: '',
    celular: '',
    
    // Configurações
    simples: false,
    assoComCertificado: false,
    incluirAssinatura: false,
    bloquearEmpresa: false,
    
    // Funções (dinâmico baseado no plano)
    funcoes: []
  });

  const estados = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  // Inicializar funções baseado no plano selecionado
  React.useEffect(() => {
    if (selectedPlan) {
      const maxFuncoes = selectedPlan.subtitle.includes('3') ? 3 :
                        selectedPlan.subtitle.includes('6') ? 6 :
                        selectedPlan.subtitle.includes('10') ? 10 : 15;
      
      const novasFuncoes = Array.from({ length: maxFuncoes }, (_, i) => ({
        id: i + 1,
        nome: '',
        descricao: ''
      }));
      
      setFormData(prev => ({ ...prev, funcoes: novasFuncoes }));
    }
  }, [selectedPlan]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFuncaoChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      funcoes: prev.funcoes.map((funcao, i) => 
        i === index ? { ...funcao, [field]: value } : funcao
      )
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Dados do formulário:', formData);
    // Aqui você enviaria os dados para o servidor
    alert('Solicitação enviada com sucesso!');
    onClose();
  };

  if (!isOpen || !selectedPlan) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <div className="modal-title-section">
            <h2 className="modal-title">Contratação do Plano {selectedPlan.name}</h2>
            <p className="modal-subtitle">{selectedPlan.subtitle} - R$ {selectedPlan.price}/mês</p>
          </div>
          <button onClick={onClose} className="modal-close">
            <X className="close-icon" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {/* Dados da Empresa */}
          <div className="form-section">
            <h3 className="section-title">
              <Building2 className="section-icon" />
              Dados da Empresa
            </h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="nome">Nome *</label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="cnpj">CNPJ *</label>
                <input
                  type="text"
                  id="cnpj"
                  name="cnpj"
                  value={formData.cnpj}
                  onChange={handleInputChange}
                  placeholder="00.000.000/0000-00"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="cei">CEI</label>
                <input
                  type="text"
                  id="cei"
                  name="cei"
                  value={formData.cei}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="inscricaoEstadual">Inscrição Estadual</label>
                <input
                  type="text"
                  id="inscricaoEstadual"
                  name="inscricaoEstadual"
                  value={formData.inscricaoEstadual}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="cnae">CNAE *</label>
                <select
                  id="cnae"
                  name="cnae"
                  value={formData.cnae}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Selecione</option>
                  <option value="8630-5/01">Atividade médica ambulatorial com recursos para realização de procedimentos cirúrgicos</option>
                  <option value="8640-2/01">Atividades de serviços de complementação diagnóstica e terapêutica</option>
                  <option value="8650-0/01">Atividades de profissionais da área de saúde</option>
                  {/* Adicione mais CNAEs conforme necessário */}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="grauRisco">Grau de Risco *</label>
                <select
                  id="grauRisco"
                  name="grauRisco"
                  value={formData.grauRisco}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Selecione</option>
                  <option value="1">Grau 1 - Risco Pequeno</option>
                  <option value="2">Grau 2 - Risco Leve</option>
                  <option value="3">Grau 3 - Risco Médio</option>
                  <option value="4">Grau 4 - Risco Grande</option>
                </select>
              </div>
            </div>
          </div>

          {/* Endereço */}
          <div className="form-section">
            <h3 className="section-title">
              <MapPin className="section-icon" />
              Endereço
            </h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="cep">CEP *</label>
                <input
                  type="text"
                  id="cep"
                  name="cep"
                  value={formData.cep}
                  onChange={handleInputChange}
                  placeholder="00000-000"
                  required
                />
              </div>
              <div className="form-group form-group-large">
                <label htmlFor="endereco">Endereço *</label>
                <input
                  type="text"
                  id="endereco"
                  name="endereco"
                  value={formData.endereco}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="numero">Número *</label>
                <input
                  type="text"
                  id="numero"
                  name="numero"
                  value={formData.numero}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="complemento">Complemento</label>
                <input
                  type="text"
                  id="complemento"
                  name="complemento"
                  value={formData.complemento}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="estado">Estado *</label>
                <select
                  id="estado"
                  name="estado"
                  value={formData.estado}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Selecione</option>
                  {estados.map(estado => (
                    <option key={estado} value={estado}>{estado}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="cidade">Cidade *</label>
                <input
                  type="text"
                  id="cidade"
                  name="cidade"
                  value={formData.cidade}
                  onChange={handleInputChange}
                  placeholder="Pesquisa..."
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="bairro">Bairro *</label>
                <input
                  type="text"
                  id="bairro"
                  name="bairro"
                  value={formData.bairro}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
          </div>

          {/* Contato */}
          <div className="form-section">
            <h3 className="section-title">
              <Phone className="section-icon" />
              Contato
            </h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="telefone">Telefone *</label>
                <input
                  type="tel"
                  id="telefone"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleInputChange}
                  placeholder="(00) 0000-0000"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="celular">Celular *</label>
                <input
                  type="tel"
                  id="celular"
                  name="celular"
                  value={formData.celular}
                  onChange={handleInputChange}
                  placeholder="(00) 00000-0000"
                  required
                />
              </div>
            </div>
          </div>

          {/* Configurações */}
          <div className="form-section">
            <h3 className="section-title">
              <User className="section-icon" />
              Configurações
            </h3>
            <div className="checkbox-grid">
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="simples"
                  name="simples"
                  checked={formData.simples}
                  onChange={handleInputChange}
                />
                <label htmlFor="simples">Optante pelo Simples Nacional</label>
              </div>
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="assoComCertificado"
                  name="assoComCertificado"
                  checked={formData.assoComCertificado}
                  onChange={handleInputChange}
                />
                <label htmlFor="assoComCertificado">Assinar ASO com Certificado</label>
              </div>
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="incluirAssinatura"
                  name="incluirAssinatura"
                  checked={formData.incluirAssinatura}
                  onChange={handleInputChange}
                />
                <label htmlFor="incluirAssinatura">Incluir Assinatura no ASO</label>
              </div>
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="bloquearEmpresa"
                  name="bloquearEmpresa"
                  checked={formData.bloquearEmpresa}
                  onChange={handleInputChange}
                />
                <label htmlFor="bloquearEmpresa">Bloquear Empresa Manualmente?</label>
              </div>
            </div>
          </div>

          {/* Funções */}
          <div className="form-section">
            <h3 className="section-title">
              <Building2 className="section-icon" />
              Funções ({formData.funcoes.length} permitidas neste plano)
            </h3>
            <div className="funcoes-list">
              {formData.funcoes.map((funcao, index) => (
                <div key={funcao.id} className="funcao-item">
                  <div className="funcao-header">
                    <span className="funcao-number">Função {index + 1}</span>
                  </div>
                  <div className="funcao-fields">
                    <div className="form-group">
                      <label htmlFor={`funcao-nome-${index}`}>Nome da Função</label>
                      <input
                        type="text"
                        id={`funcao-nome-${index}`}
                        value={funcao.nome}
                        onChange={(e) => handleFuncaoChange(index, 'nome', e.target.value)}
                        placeholder="Ex: Auxiliar Administrativo"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor={`funcao-descricao-${index}`}>Descrição</label>
                      <textarea
                        id={`funcao-descricao-${index}`}
                        value={funcao.descricao}
                        onChange={(e) => handleFuncaoChange(index, 'descricao', e.target.value)}
                        placeholder="Descreva as atividades da função..."
                        rows="3"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Botões */}
          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary">
              Solicitar Contratação
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlanFormModal;